import getFromCdn from '@anm/helpers/getFromCdn';
import { NextInitialProps } from 'anm-next';
import React from 'react';
import { compose } from 'recompose';

import bootstrapPage, { BootstrapPageProps } from '../HOCs/bootstrapPage';
import injectSagasAndReducers from '../HOCs/injectSagasAndReducers';
import CalendarEvents from '../components/CalendarEvents';
import MetaHead from '../components/MetaHead';
import Layout from '../components/layouts/Layout';
import categoryReducer from '../store/modules/category';
import collapseReducer, { collapseSaga } from '../store/modules/collapse';
import filterReducer from '../store/modules/filter';
import videoTemplateReducer, { templateSaga } from '../store/modules/template';
import waveEventsReducer, { waveEventsActions, waveEventsSaga } from '../store/modules/waveEvents';

import Page404 from './404';

type CalendarPageQuery = {
  eventId?: string;
  weekDate?: string;
};

const isPage404 = (weekDate?: string) => weekDate && new Date(weekDate).toDateString() === 'Invalid Date';

type CalendarPageProps = BootstrapPageProps & {
  weekDate?: string;
};
const CalendarPage = ({ path, weekDate }: CalendarPageProps) => {
  const title = 'Free Social Calendar';
  const description =
    'Looking for content to share on social? Never run out of content ideas with this free social media calendar. Find popular holidays, events, and hashtags. Free templates included!';

  const previewUrl = `${getFromCdn('images/data/calendar/social_calendar_preview.jpg')}`;
  const previewSize = { width: 400, height: 300 };

  if (isPage404(weekDate)) return <Page404 />;

  return (
    <>
      <MetaHead path={path} size={previewSize} title={title} preview={previewUrl} description={description} />
      <Layout navigationVariant="white">
        <CalendarEvents />
      </Layout>
    </>
  );
};

CalendarPage.getInitialProps = async (ctx: NextInitialProps<CalendarPageQuery>) => {
  const { query, store, res } = ctx;
  const { weekDate } = query;
  store.dispatch(waveEventsActions.fetchWaveEventList(weekDate));

  const onAsyncDone = () => {
    if (res && isPage404(weekDate)) {
      res.statusCode = 404;
    }
  };

  return { weekDate, onAsyncDone };
};

export default compose<BootstrapPageProps, {}>(
  bootstrapPage,
  injectSagasAndReducers(
    () => [...collapseSaga(), ...templateSaga(), ...waveEventsSaga()],
    {
      filter: filterReducer,
      collapse: collapseReducer,
      category: categoryReducer,
      template: videoTemplateReducer,
      waveEvents: waveEventsReducer
    },
    { id: 'CalendarPage' }
  )
)(CalendarPage);
